import { ClientKiwiSaverGuard } from './client-kiwisaver.guard';
import { SiteIds } from './../../models/sites';
import { ClientHomeRouteService } from './../utils/client-home-route.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs/internal/observable/of';
import { Router, Event, ActivatedRouteSnapshot, RouterStateSnapshot, NavigationEnd } from '@angular/router';
import { PortfoliosFacade } from 'src/app/store/portfolios/facade/portfolios.facade';
import { selectSelectedClientGroup } from 'src/app/store/client/selectors/client.selectors';
import { ClientFacade } from './../../store/client/facade/client.facade';
import * as GeneralSelectionActions from 'src/app/store/general-selection/actions/general-selection.actions';
import { IAppState } from 'src/app/store/state/app.state';
import { selectGeneralSelectionDate } from 'src/app/store/general-selection/selectors/general-selection.selectors';
import { IClient } from 'src/app/store/client/reducers/client.reducers';
import { TokenService } from '../token/token.service';
import { AppConfig } from 'src/app/app.config';
import { UserRoleType } from "src/app/models/userRoleTypes";
import { AuthService } from "../http/auth.service";

@Injectable()
export class ClientGuard  implements OnDestroy {

    previousUrl: string;
    routerSubscription$: Subscription;
    clientsSelected = false;
    generalSelectionSubscription$: Subscription;
    clientSubscription$: Subscription;
    isAdministrator = false;
    isAdvisor = false;
    selectedDate: Date;

    constructor(
        public tokenService: TokenService,
        private router: Router,
        private store: Store<IAppState>,
        private appConfig: AppConfig,
        private portfoliosFacade: PortfoliosFacade,
        private clientFacade: ClientFacade,
        private clientKiwiSaverGuard: ClientKiwiSaverGuard,
        private clientHomeRouteService: ClientHomeRouteService,
        private authService: AuthService) {
        this.routerSubscription$ = this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd && !this.clientsSelected) {
                this.store.dispatch(new GeneralSelectionActions.UpdateBaseUrl(event.url));
            }
        });

        this.isAdministrator = this.tokenService.isAdministrator();
        this.isAdvisor = this.authService.doesUserHaveRole(UserRoleType.Advisor);

        this.generalSelectionSubscription$ = this.store.pipe(select(selectGeneralSelectionDate)).subscribe((d: Date) => {
            this.selectedDate = d;
        });

        this.clientSubscription$ = this.store.pipe(select(selectSelectedClientGroup)).subscribe((group: IClient) => {
            this.clientsSelected = (group.GroupId !== 0 || group.Members.length > 0);
        });

        if (!this.isAdministrator && !this.isAdvisor) {
            // TODO. we need to check if the user is actually logged in, and if there are already portfolios loaded.
            // This needs to be refactored. The client guard is not the place to put this logic.
            this.portfoliosFacade.getInvestorPortfolios();
        }
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        this.isAdministrator = this.tokenService.isAdministrator();
        this.isAdvisor = this.authService.doesUserHaveRole(UserRoleType.Advisor);
        return this.getClientsAndEntities(route, state)
            .pipe(switchMap((result) => {
                if (result) {
                    return of(true);
                } else {
                    return of(false);
                }
            }));
    }

    private getClientsAndEntities(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const siteId = this.appConfig.appConfigSignal().env.siteId;
        if (siteId && (siteId === SiteIds.INVESTNOW || siteId === SiteIds.EVIDENTIAL)) {
            return this.clientKiwiSaverGuard.canActivate(route, state);
        } else if (this.clientsSelected) {
            return of(true);
        } else {
            if (!this.isAdministrator && !this.isAdvisor) {
                // This drives all client data being retrieved! Needs to be refactored
                // TODO: #entity refactor
                this.clientFacade.getClientInvestorEntities(this.selectedDate);
                return of(true);
            } else {
                this.clientHomeRouteService.redirectToHome();
                return of(false);
            }
        }
    }

    ngOnDestroy() {
        this.routerSubscription$.unsubscribe();
        this.generalSelectionSubscription$.unsubscribe();
        this.clientSubscription$.unsubscribe();
    }
}
